/**
 * ColorPicker Component
 * 
 * Color picker with predefined palette and custom hex input
 * Shared component used by CategoryForm and TagForm
 */

import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

// Calculate relative luminance for WCAG contrast checking
function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  usageCounts?: Record<string, number>;
  disabled?: boolean;
}

// WCAG AA accessible colors organized logically by hue (Red -> Orange -> Yellow -> Green -> Blue -> Purple -> Pink)
// All colors meet minimum 4.5:1 contrast ratio with white text
const PREDEFINED_COLORS = [
  // Reds
  '#7F1D1D', '#B91C1C', '#E11D48', '#9F1239',
  // Oranges
  '#7C2D12', '#9A3412', '#C2410C',
  // Yellows (dark ambers that pass on white)
  '#854D0E', '#A16207', '#7A5800',
  // Greens
  '#14532D', '#166534', '#047857', '#0F766E',
  // Blues
  '#1E3A8A', '#1D4ED8', '#0B5ED7', '#1E293B',
  // Purples
  '#4C1D95', '#5B21B6', '#6D28D9',
  // Pinks
  '#831843', '#9D174D', '#BE185D', '#DB2777',
];

export function ColorPicker({ value, onChange, label = 'Color', usageCounts = {}, disabled = false }: ColorPickerProps): React.ReactElement {
  const [customColor, setCustomColor] = useState(value && !PREDEFINED_COLORS.includes(value) ? value : '');
  const [isOpen, setIsOpen] = useState(false);

  const handlePredefinedColorClick = (color: string) => {
    onChange(color);
    setCustomColor('');
    setIsOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    if (/^#[0-9A-Fa-f]{6}$/.test(newColor)) {
      onChange(newColor);
    }
  };

  const selectedColorLabel = useMemo(() => {
    if (value && PREDEFINED_COLORS.includes(value)) {
      const count = usageCounts[value] ?? 0;
      return `${value.toUpperCase()}${count > 0 ? ` • ${count} category${count === 1 ? '' : 'ies'}` : ''}`;
    }
    if (value) {
      return `${value.toUpperCase()} • Custom`;
    }
    return 'Choose a color';
  }, [value, usageCounts]);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          disabled={disabled}
          className={`flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition ${
            disabled
              ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
              : 'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500'
          } dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
        >
          <span className="flex items-center space-x-3">
            <span
              className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-500"
              style={{ backgroundColor: value || '#ffffff' }}
              aria-hidden="true"
            />
            <span>{selectedColorLabel}</span>
          </span>
          <ChevronDown className={clsx('h-4 w-4 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
        </button>

        {isOpen && (
          <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="max-h-64 overflow-y-auto">
              <ul className="divide-y divide-gray-100 text-sm dark:divide-gray-700" role="listbox">
                {PREDEFINED_COLORS.map((color) => {
                  const isLight = getLuminance(color) > 0.5;
                  const checkColor = isLight ? '#000000' : '#FFFFFF';
                  const checkStyle = isLight ? {} : { filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' };
                  const count = usageCounts[color] ?? 0;
                  const isSelected = value === color;

                  return (
                    <li key={color}>
                      <button
                        type="button"
                        onClick={() => handlePredefinedColorClick(color)}
                        className={clsx(
                          'flex w-full items-center justify-between px-4 py-2 transition hover:bg-gray-50 dark:hover:bg-gray-700/50',
                          isSelected && 'bg-primary-50 dark:bg-primary-900/10'
                        )}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="flex items-center space-x-3">
                          <span
                            className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-500"
                            style={{ backgroundColor: color }}
                            aria-hidden="true"
                          />
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {color.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {count} {count === 1 ? 'category' : 'categories'}
                          </span>
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4" style={{ color: checkColor, ...checkStyle }} aria-hidden="true" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Custom Color Input */}
      <div className="flex items-center space-x-2">
        <label className={`text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>Custom:</label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => !disabled && onChange(e.target.value)}
            disabled={disabled}
            className={`w-12 h-10 rounded border border-gray-300 dark:border-gray-600 ${
              disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
            }`}
            aria-label="Custom color picker"
            aria-disabled={disabled}
          />
          <input
            type="text"
            value={customColor || value || ''}
            onChange={handleCustomColorChange}
            placeholder="#FF5733"
            pattern="^#[0-9A-Fa-f]{6}$"
            disabled={disabled}
            className={`w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              disabled ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''
            }`}
            aria-label="Custom hex color code"
            aria-disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

